import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AssistantInput from '@/components/common/composer/AssistantInput.tsx';
import ChatContainer from './chat/ChatContainer.tsx';
import AssistantQuerySelector from '@/components/dashbaord/assistant/query-selector/AssistantQuerySelector.tsx';
import AssistantHeading, { AssistantHeadingMode } from '@/components/dashbaord/assistant/AssistantHeading.tsx';
import { Box, HStack, useBreakpointValue, VStack } from '@chakra-ui/react';
import { useAssistant, useAssistantInput } from '@/components/dashbaord/assistant/AssistantContext.tsx';
import { SHOW_TABS_IN_CHAT_FEATURE_FLAG } from '@/components/dashbaord/assistant/featureFlags.ts';
import ChatHistoryDrawer from '@/components/assistant/history/ChatHistoryDrawer.tsx';
import { useHouseContext } from '@/context/HouseContext.tsx';
import { useChatHistory } from '@/context/ChatHistoryContext.tsx';
import ComposerDock from '@/components/common/composer/ComposerDock.tsx';
import { ASSISTANT_CONTENT_WIDTH } from '@/components/common/composer/composerConstants.ts';
import { useNavigate, useSearch } from '@tanstack/react-router';
import JumpToBottomButton from '@/components/dashbaord/assistant/JumpToBottomButton.tsx';
import { useJumpToBottom } from '@/components/dashbaord/assistant/useJumpToBottom.ts';
import { usePageScrollContainerRef } from '@/context/PageScrollContainerContext.ts';
import { useUpdateStatus } from '@/context/UpdateStatusContext.tsx';
import { useDeepLinkSessionResume } from '@/hooks/assistant/useDeepLinkSessionResume';
import { useAskReadinessEpisodeDismissal } from '@/components/common/composer/useAskReadinessEpisodeDismissal';
import { useAssistantSessionUrlSync } from '@/hooks/assistant/useAssistantSessionUrlSync';

const DOCK_CLEARANCE = '128px';
const ACTIVE_EXCHANGE_SCROLL_ROOM = '45dvh';
const ACTIVE_EXCHANGE_VIEWPORT_OFFSET_RATIO = 0.45;
const EMPTY_STATE_DESKTOP_TOP_OFFSET = { md: '32vh' } as const;

interface AssistantContainerProps {
	suppressAskReadiness?: boolean;
}

/**
 * Main assistant interface component
 *
 * Displays the query form, response display, and query tabs
 */
const AssistantContainer: React.FC<AssistantContainerProps> = ({ suppressAskReadiness = false }) => {
	const isMobile = useBreakpointValue({ base: true, md: false });
	const [headingMode, setHeadingMode] = useState<AssistantHeadingMode>('ask');
	const { selectedHouse } = useHouseContext();
	const { knowledgeBaseReadinessState, knowledgeBaseReadinessSummary } = useUpdateStatus();
	const { isOpen: isChatHistoryOpen, close: closeChatHistory } = useChatHistory();
	const containerRef = useRef<HTMLDivElement>(null);
	const activeExchangeRef = useRef<HTMLDivElement>(null);
	const pageScrollRef = usePageScrollContainerRef();
	const scrolledExchangeIdRef = useRef<string | null>(null);
	const navigate = useNavigate();
	const { questionId, q } = useSearch({ from: '/_authenticated/property/$propertyPublicId/' });
	const {
		documents,
		documentsQuery,
		queries,
		submitQueryText,
		activeQuery,
		isQueryActive,
		chatMode,
		isExecutingQuery,
		showCitationPills,
		handleExistingQueryClick,
		resumeSession,
		currentSessionId,
		resetAssistant,
	} = useAssistant();
	const { inputQuery, setInputQuery, handleQuerySubmit } = useAssistantInput();
	const jumpToBottom = useJumpToBottom({
		pageScrollRef,
		contentRef: containerRef,
		enabled: chatMode,
		contentVersion: `${queries.length}:${activeQuery?.id ?? ''}:${isExecutingQuery}`,
		resetKey: activeQuery?.id ?? '',
	});

	// Once the session has a prompt, the composer is for follow-ups — nudge the placeholder so it reads
	// as continuing the conversation rather than starting one.
	const askPlaceholder = queries.length > 0 ? 'Ask a new question...' : 'Ask a question...';
	const askReadinessState = suppressAskReadiness ? null : knowledgeBaseReadinessState;
	const askReadinessContext = useMemo(() => {
		if (!askReadinessState || !knowledgeBaseReadinessSummary?.stalled) {
			return undefined;
		}
		const affectedFileCount = knowledgeBaseReadinessSummary.stalled;
		const stalledDocumentId =
			affectedFileCount === 1 && knowledgeBaseReadinessSummary.attentionDocumentIds.length === 1
				? knowledgeBaseReadinessSummary.attentionDocumentIds[0]
				: undefined;
		const filePublicId =
			stalledDocumentId === undefined
				? undefined
				: documents.find((document) => document.id === stalledDocumentId)?.public_id;
		return { affectedFileCount, filePublicId: filePublicId ?? undefined };
	}, [askReadinessState, documents, knowledgeBaseReadinessSummary]);
	const askReadinessDismissal = useAskReadinessEpisodeDismissal({
		houseId: selectedHouse?.id ?? null,
		state: askReadinessState,
		attentionDocumentIds: knowledgeBaseReadinessSummary?.attentionDocumentIds ?? [],
	});
	const currentSessionFirstQuery = currentSessionId
		? queries.find((query) => query.sessionId === currentSessionId)
		: undefined;
	const currentSessionTitle = currentSessionFirstQuery?.text.trim() || 'New chat';
	const currentSessionCreatedAt = currentSessionFirstQuery?.createdAt.toISOString() ?? null;

	/**
	 * Reset the assistant whenever questionId is absent from the URL.
	 * This covers both the initial mount and the case where the user clicks
	 * "Ask" in the sidebar while already on the dashboard (no remount occurs).
	 *
	 * `resetAssistant` is deliberately excluded from the deps: it isn't reference
	 * stable, so including it would re-run this effect every render and, while
	 * questionId is absent, wipe inputQuery on every keystroke.
	 */
	useEffect(() => {
		if (!questionId) {
			resetAssistant();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [questionId]);

	const syncStartedSessionToUrl = useCallback(
		(sessionId: string) => {
			void navigate({
				search: (prev: Record<string, unknown>) => ({ ...prev, questionId: sessionId }),
				replace: true,
			} as never);
		},
		[navigate],
	);
	// TURN_STARTED attaches the backend session id to the first local query. Persist that null-to-id
	// transition into this history entry so Browser Back can address a chat started on this page.
	useAssistantSessionUrlSync({ currentSessionId, questionId, onSessionStarted: syncStartedSessionToUrl });

	// Make a conversation addressable by URL: ?questionId= resumes that session on load.
	useDeepLinkSessionResume(selectedHouse?.id, questionId, currentSessionId, isExecutingQuery, resumeSession);

	/**
	 * Chat handoff from another screen: ?q=… auto-submits the query, so a single submit on the
	 * source screen (e.g. records search) both navigates here and runs the answer — no second Enter.
	 *
	 * setTimeout(0) defers the submit past AssistantProvider's mount reset (parent effects run after
	 * child effects on mount, so a synchronous call gets clobbered). The URL strip runs in the same
	 * tick so a refresh doesn't re-trigger the submit.
	 */
	useEffect(() => {
		if (!q) return;
		const handle = setTimeout(() => {
			void submitQueryText(q);
			navigate({
				search: (prev: Record<string, unknown>) => ({ ...prev, q: undefined }),
				replace: true,
			} as never);
		}, 0);
		return () => clearTimeout(handle);
	}, [q, submitQueryText, navigate]);

	/** Sync the active session ID into the URL as ?questionId= when resuming from chat history */
	const handleResumeSession = (sessionId: string, exchanges: Parameters<typeof resumeSession>[1]) => {
		resumeSession(sessionId, exchanges);
		navigate({ search: () => ({ questionId: sessionId }), replace: true } as never);
	};

	useEffect(() => {
		if (!chatMode || !activeQuery) return;
		if (scrolledExchangeIdRef.current === activeQuery.id) return;

		const frameId = requestAnimationFrame(() => {
			const activeExchange = activeExchangeRef.current;
			const scrollContainer = pageScrollRef?.current;
			if (!activeExchange || !scrollContainer) return;

			scrolledExchangeIdRef.current = activeQuery.id;
			const exchangeTop = activeExchange.getBoundingClientRect().top;
			const containerTop = scrollContainer.getBoundingClientRect().top;
			scrollContainer.scrollTo({
				top:
					scrollContainer.scrollTop +
					exchangeTop -
					containerTop -
					scrollContainer.clientHeight * ACTIVE_EXCHANGE_VIEWPORT_OFFSET_RATIO,
				behavior: 'auto',
			});
		});

		return () => cancelAnimationFrame(frameId);
	}, [chatMode, activeQuery?.id, pageScrollRef]);

	return (
		<VStack
			ref={containerRef}
			className="dashboard-assisant-wrapper"
			gap={8}
			align="center"
			// Desktop empty state is top-anchored (in the upper-centre via the pt below)
			// so opening the add composer grows DOWN — tabs stay put, no heading overlap.
			// Tune the `pt` value to raise/lower where the collapsed bar sits.
			justify={chatMode ? 'start' : isMobile ? 'center' : 'flex-start'}
			w="full"
			p={{ base: 4, md: 0 }}
			pb={!chatMode && isMobile ? DOCK_CLEARANCE : undefined}
			pt={!chatMode && !isMobile ? EMPTY_STATE_DESKTOP_TOP_OFFSET : undefined}
			minH="100%"
		>
			{selectedHouse && (
				<ChatHistoryDrawer
					isOpen={isChatHistoryOpen}
					onClose={closeChatHistory}
					houseId={selectedHouse.id}
					onResumeSession={handleResumeSession}
					currentSessionId={currentSessionId}
					currentSessionTitle={currentSessionTitle}
					currentSessionCreatedAt={currentSessionCreatedAt}
					currentSessionIsStreaming={isExecutingQuery}
				/>
			)}
			<VStack className="assistant-container" gap={8} align="center" p={0} w={ASSISTANT_CONTENT_WIDTH} mx="auto">
				{/* Show heading only if not in query mode */}
				{!isQueryActive && <AssistantHeading mode={headingMode} />}
				{/* Render all queries as a conversation thread */}
				{queries.map((query) => {
					const isActiveExchange = query.id === activeQuery?.id;
					return (
						<React.Fragment key={query.id}>
							<Box
								ref={isActiveExchange ? activeExchangeRef : undefined}
								w="100%"
								h={chatMode && isActiveExchange ? 'full' : 'auto'}
								className="assistant-chat-container"
							>
								<ChatContainer
									documents={documents}
									query={query}
									chatMode={chatMode}
									showCitationPills={showCitationPills}
									onSubmitPendingReply={submitQueryText}
								/>
							</Box>
							{isActiveExchange && isExecutingQuery && (
								<Box aria-hidden w="100%" h={ACTIVE_EXCHANGE_SCROLL_ROOM} />
							)}
						</React.Fragment>
					);
				})}
				{/* Centered "hero" input — desktop only; mobile keeps it bottom-fixed. */}
				{!chatMode && !isMobile && (
					<Box w="100%" maxW="720px" mx="auto">
						<VStack gap={2} w="100%">
							{(isMobile || isQueryActive) && SHOW_TABS_IN_CHAT_FEATURE_FLAG && (
								<AssistantQuerySelector onQueryClick={handleExistingQueryClick} />
							)}
							<HStack w="100%" align="center" gap={1}>
								<Box flex="1" minW={0}>
									<AssistantInput
										query={inputQuery}
										setQuery={setInputQuery}
										onSubmit={handleQuerySubmit}
										isLoadingData={documentsQuery.isLoading}
										isRunning={isExecutingQuery}
										onHeadingModeChange={setHeadingMode}
										placeholder={askPlaceholder}
										askReadinessState={askReadinessState}
										askReadinessContext={askReadinessContext}
										askReadinessDismissed={askReadinessDismissal.dismissed}
										onDismissAskReadiness={askReadinessDismissal.dismiss}
										expandsDown
									/>
								</Box>
							</HStack>
							{!isMobile && !isQueryActive && SHOW_TABS_IN_CHAT_FEATURE_FLAG && (
								<AssistantQuerySelector onQueryClick={handleExistingQueryClick} />
							)}
						</VStack>
					</Box>
				)}
				{/* Conversation content needs room beneath its final exchange for the fixed dock.
				    The empty mobile state reserves that room on the outer centering region instead. */}
				{chatMode && <Box w="100%" h={DOCK_CLEARANCE} />}
				{/* Fixed-bottom dock (shared with the records page). Mounted in chat mode, and always
				    on mobile; slides up from the bottom edge on entry. The jump-to-latest button rides
				    in the dock's aboveBar slot so it floats just above the bar at any height. */}
				{(chatMode || isMobile) && (
					<ComposerDock
						slideUpOnMount
						aboveBar={
							chatMode && jumpToBottom.isVisible ? (
								<JumpToBottomButton
									isCollapsing={jumpToBottom.isCollapsing}
									isStreaming={isExecutingQuery}
									onClick={jumpToBottom.jumpToBottom}
								/>
							) : undefined
						}
					>
						<VStack gap={2} w="100%" className="assistant-input-container">
							{(isMobile || isQueryActive) && SHOW_TABS_IN_CHAT_FEATURE_FLAG && (
								<AssistantQuerySelector onQueryClick={handleExistingQueryClick} />
							)}
							<HStack w="100%" align="center" gap={1}>
								<Box flex="1" minW={0}>
									<AssistantInput
										query={inputQuery}
										setQuery={setInputQuery}
										onSubmit={handleQuerySubmit}
										isLoadingData={documentsQuery.isLoading}
										isRunning={isExecutingQuery}
										onHeadingModeChange={setHeadingMode}
										placeholder={askPlaceholder}
										askReadinessState={askReadinessState}
										askReadinessContext={askReadinessContext}
										askReadinessDismissed={askReadinessDismissal.dismissed}
										onDismissAskReadiness={askReadinessDismissal.dismiss}
										autoFocus={false}
									/>
								</Box>
							</HStack>
							<Box
								textStyle="micro"
								color="fg.subtle"
								textAlign="center"
								px={4}
								mt={-2}
								transform={{ base: 'translateY(4px)', md: 'none' }}
							>
								Bundle is not infallible - {isMobile ? '' : 'please'} check important information.
							</Box>
						</VStack>
					</ComposerDock>
				)}
			</VStack>
		</VStack>
	);
};

export default AssistantContainer;
